import { FC, useCallback, useRef } from 'react';
import { UploadRequestOption } from 'rc-upload/es/interface';
import {
  ApiErrorResponse,
  ClassifierFormDataType,
  ClassifierUploadSection,
  Popconfirm,
  ProgrammaticUpload,
  ProgrammaticUploadRef,
  useMutateClassifierUpload,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ClassifierUpload.translations.json';
import { Button } from 'antd';
import * as Icon from 'react-feather';

const { addTranslations, useTranslation } = i18nNsFactory('classifier-upload');
addTranslations(translations);

export interface ClassifierUploadProps {
  classifierId?: number;
  sections: ClassifierUploadSection[];
  label: string;
  triggerVariant?: 'label' | 'button';
  confirmMessage?: string;
  onProgress?: (progress: number) => void;
  onError?: (error: ApiErrorResponse) => void;
  onSuccess: (classifier: Partial<ClassifierFormDataType>) => void;
}

export const ClassifierUpload: FC<ClassifierUploadProps> = ({
  classifierId,
  sections,
  label,
  triggerVariant = 'button',
  confirmMessage,
  onError,
  onProgress,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const uploadMainRef = useRef<ProgrammaticUploadRef | null>(null);

  const { mutate } = useMutateClassifierUpload(onProgress);
  const handleUpload = useCallback(
    (options: UploadRequestOption) => {
      const { file } = options;
      mutate(
        { sections, file, id: classifierId },
        {
          onSuccess,
          onError,
        },
      );
    },
    [mutate, sections, classifierId, onSuccess, onError],
  );

  const handleUploadInit = useCallback(() => {
    uploadMainRef.current?.startUpload();
  }, []);

  const trigger =
    triggerVariant === 'label' ? (
      <span onClick={!confirmMessage ? handleUploadInit : undefined}>
        {label}
      </span>
    ) : (
      <Button
        id="upload-classifier"
        icon={<Icon.Upload />}
        onClick={!confirmMessage ? handleUploadInit : undefined}
      />
    );

  return (
    <div>
      {confirmMessage ? (
        <Popconfirm
          id={`confirm-upload-classifier-${sections.join('-')}`}
          title={confirmMessage}
          description={t('description')}
          okText={t('button.yes')}
          cancelText={t('button.no')}
          style={{ maxWidth: 300 }}
          onConfirm={handleUploadInit}
        >
          {trigger}
        </Popconfirm>
      ) : (
        trigger
      )}
      <ProgrammaticUpload
        id={`classifier-${sections.join('-')}-upload`}
        customRequest={handleUpload}
        showUploadList={false}
        ref={uploadMainRef}
      />
    </div>
  );
};
